/**
 * BonsKpiHeader — Bandeau sticky en tête du menu "Bons" (employés & Resp. Op.).
 *
 * Affiche les 4 KPI clés cliquables :
 *  - À retenir sur salaire (authorized + settled)
 *  - En attente de décision (pending_manager + pending_director)
 *  - Non autorisés (cancelled)
 *  - Ce mois (info + plafond)
 *
 * 🆕 Fév 2026 : les 3 premiers KPI sont cliquables → ouvrent une modale avec
 * répartition par agent + liste chronologique complète des bons concernés.
 * Les valeurs sont recalculées depuis la liste `orders` (source unique) pour
 * garantir la cohérence avec les bulles agents.
 */
import React, { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Wallet, Clock, XCircle, Calendar as CalIcon, X, Users, ChefHat } from "lucide-react";

const KPI_META = {
  authorized: {
    Icon: Wallet, tone: "emerald", label: "Achats autorisés",
    // Un bon est "à retenir" quand il est autorisé ou déjà réglé
    matches: (o) => o.status === "authorized" || o.status === "settled",
    amountKey: "total", // montant réel appliqué (avec remise)
    title: "Achats autorisés — détail",
  },
  pending: {
    Icon: Clock, tone: "amber", label: "En attente de décision",
    matches: (o) => o.status === "pending_manager" || o.status === "pending_director",
    amountKey: "subtotal", // prix normal tant que non autorisé
    title: "Bons en attente de décision — détail",
  },
  not_authorized: {
    Icon: XCircle, tone: "red", label: "Non autorisés (dû)",
    matches: (o) => o.status === "cancelled",
    amountKey: "subtotal", // prix normal (sans remise)
    title: "Bons non autorisés — détail",
  },
};

const BonsKpiHeader = ({
  filterMonth,
  orders = [],                     // 🆕 source de vérité pour recalculer les totaux
  maxPerMonth,
  formatPrice = (v) => Number(v || 0).toLocaleString("fr-FR"),
  accent = "violet",
  rightSlot = null,
  monthSlot = null,
  dataTestId = "bons-kpi-header",
}) => {
  const [openKey, setOpenKey] = useState(null);
  const c = accent === "pink"
    ? { border: "border-pink-500/40", title: "text-pink-300" }
    : { border: "border-violet-500/40", title: "text-violet-300" };

  // 🆕 Recalcul client-side pour garantir la cohérence avec les bulles agents
  const kpiData = useMemo(() => {
    const out = {};
    for (const [key, meta] of Object.entries(KPI_META)) {
      const matched = (orders || []).filter(meta.matches);
      out[key] = {
        ...meta,
        count: matched.length,
        amount: matched.reduce((s, o) => s + Number(o[meta.amountKey] || 0), 0),
        orders: matched,
      };
    }
    return out;
  }, [orders]);

  const kpiTiles = [
    { key: "authorized", ...kpiData.authorized, hint: `${kpiData.authorized.count} bon(s) autorisé(s)/réglé(s)` },
    { key: "pending", ...kpiData.pending, hint: `${kpiData.pending.count} bon(s) à valider` },
    { key: "not_authorized", ...kpiData.not_authorized, hint: `${kpiData.not_authorized.count} bon(s) refusé(s)` },
    { key: "month", Icon: CalIcon, tone: "slate", label: filterMonth || "—", amount: null, count: null, hint: maxPerMonth ? `Plafond ${formatPrice(maxPerMonth)} F/mois` : "", noClick: true },
  ];

  return (
    <>
      <div
        className={`sticky top-0 z-30 -mx-3 sm:-mx-4 px-3 sm:px-4 pt-1 pb-3 bg-gradient-to-b from-slate-950 via-slate-950/95 to-slate-950/70 backdrop-blur-md border-b ${c.border}`}
        data-testid={dataTestId}
      >
        {monthSlot && <div className="mb-2">{monthSlot}</div>}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 flex-1 min-w-0">
            {kpiTiles.map(({ key, Icon, tone, label, amount, hint, noClick }) => (
              <button
                key={key}
                type="button"
                disabled={noClick}
                onClick={() => !noClick && setOpenKey(key)}
                className={`text-left transition-all ${noClick ? "cursor-default" : "cursor-pointer hover:-translate-y-0.5"}`}
                data-testid={`${dataTestId}-kpi-${key}`}
              >
                <Card className={`bg-${tone}-900/15 border-${tone}-500/30 ${!noClick ? `hover:border-${tone}-500/60 hover:shadow-lg` : ""} transition-colors`}>
                  <CardContent className="p-2.5 sm:p-3">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <Icon className={`w-3.5 h-3.5 text-${tone}-300`} />
                      <p className={`text-${tone}-200 text-[10.5px] sm:text-[11px] font-bold uppercase tracking-wider truncate`}>{label}</p>
                    </div>
                    {amount != null ? (
                      <p className={`text-${tone}-100 text-lg sm:text-2xl font-black tabular-nums leading-tight`}>
                        {formatPrice(amount)}<span className={`text-${tone}-300/70 text-[10px] ml-0.5`}>F</span>
                      </p>
                    ) : (
                      <p className={`text-${tone}-100 text-lg sm:text-2xl font-black leading-tight truncate`}>{filterMonth || "—"}</p>
                    )}
                    <p className={`text-${tone}-400/80 text-[9.5px] sm:text-[10px] truncate`}>
                      {hint}{!noClick && <span className="ml-1 opacity-70">· cliquer →</span>}
                    </p>
                  </CardContent>
                </Card>
              </button>
            ))}
          </div>
          {rightSlot && <div className="flex items-center gap-2 flex-shrink-0">{rightSlot}</div>}
        </div>
      </div>

      {/* Modale détail au clic sur un KPI */}
      <Dialog open={!!openKey} onOpenChange={(v) => { if (!v) setOpenKey(null); }}>
        <DialogContent
          className={`bg-slate-900 border-slate-700 text-white rounded-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-6`}
          style={{ width: "min(calc(100vw - 1.5rem), 48rem)" }}
          data-testid={`${dataTestId}-detail-modal`}
        >
          {openKey && (() => {
            const meta = kpiData[openKey];
            const { tone } = meta;
            // Répartition par agent
            const byAgent = new Map();
            for (const o of meta.orders) {
              const key = o.employee_name || "—";
              if (!byAgent.has(key)) byAgent.set(key, { name: key, position: o.employee_position || "", count: 0, amount: 0, orders: [] });
              const b = byAgent.get(key);
              b.count += 1;
              b.amount += Number(o[meta.amountKey] || 0);
              b.orders.push(o);
            }
            const perAgent = Array.from(byAgent.values()).sort((a, b) => b.amount - a.amount);
            const sorted = [...meta.orders].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
            return (
              <>
                <DialogHeader>
                  <DialogTitle className={`text-${tone}-300 flex items-center gap-2 text-lg`}>
                    <meta.Icon className="w-5 h-5" />
                    {meta.title}
                    <span className={`ml-auto rounded-full px-3 py-1 border bg-${tone}-500/20 border-${tone}-500/40 text-${tone}-100 text-[13px] font-black tabular-nums`}>
                      {formatPrice(meta.amount)} F
                    </span>
                  </DialogTitle>
                  <p className="text-slate-400 text-[12px]">{meta.count} bon{meta.count > 1 ? "s" : ""} · {perAgent.length} agent{perAgent.length > 1 ? "s" : ""}</p>
                </DialogHeader>

                {/* Répartition par agent */}
                {perAgent.length > 0 && (
                  <div className="mt-3">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Users className={`w-3.5 h-3.5 text-${tone}-300`} />
                      <p className={`text-[11px] font-bold uppercase tracking-wider text-${tone}-200`}>Répartition par agent</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid={`${dataTestId}-detail-per-agent`}>
                      {perAgent.map((a) => (
                        <div
                          key={a.name}
                          className={`rounded-lg border bg-${tone}-900/10 border-${tone}-500/25 px-2.5 py-1.5 flex items-center justify-between`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-white text-[12.5px] font-bold truncate leading-tight">{a.name}</p>
                            <p className="text-slate-500 text-[10px] truncate">{a.position || "—"} · {a.count} bon{a.count > 1 ? "s" : ""}</p>
                          </div>
                          <p className={`text-${tone}-100 text-[13px] font-black tabular-nums flex-shrink-0`}>{formatPrice(a.amount)} F</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Liste chronologique complète */}
                <div className="mt-4">
                  <div className="flex items-center gap-2 mb-1.5">
                    <CalIcon className={`w-3.5 h-3.5 text-${tone}-300`} />
                    <p className={`text-[11px] font-bold uppercase tracking-wider text-${tone}-200`}>Détail chronologique</p>
                  </div>
                  <div className="space-y-1.5 max-h-[380px] overflow-y-auto pr-1" data-testid={`${dataTestId}-detail-list`}>
                    {sorted.map((o) => {
                      const dateStr = (o.created_at || "").slice(0, 10);
                      const timeStr = (o.created_at || "").slice(11, 16);
                      const items = (o.items || []).map((it) => `${it.quantity}× ${it.name}`).join(" · ");
                      const amt = Number(o[meta.amountKey] || 0);
                      return (
                        <div
                          key={o.id}
                          className={`rounded-md border bg-slate-800/50 border-slate-700 px-2.5 py-1.5 flex items-start justify-between gap-2`}
                          data-testid={`${dataTestId}-detail-order-${o.id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] text-slate-400 flex items-center gap-1.5">
                              <span className="tabular-nums">{dateStr} · {timeStr}</span>
                              <span className="text-slate-600">·</span>
                              <span className="text-white text-[11.5px] font-bold truncate">{o.employee_name || "—"}</span>
                            </p>
                            <p className="text-slate-300 text-[11.5px] flex items-start gap-1 mt-0.5">
                              <ChefHat className="w-3 h-3 text-slate-500 flex-shrink-0 mt-0.5" />
                              <span className="line-clamp-1">{items || "—"}</span>
                            </p>
                          </div>
                          <p className={`text-${tone}-200 text-[13px] font-black tabular-nums flex-shrink-0`}>{formatPrice(amt)} F</p>
                        </div>
                      );
                    })}
                    {sorted.length === 0 && (
                      <p className="text-slate-500 text-[12px] italic text-center py-4">Aucun bon dans cette catégorie.</p>
                    )}
                  </div>
                </div>

                <div className="mt-3 flex justify-end">
                  <Button
                    variant="outline"
                    className="border-slate-600 text-slate-300 hover:bg-slate-700 h-9"
                    onClick={() => setOpenKey(null)}
                    data-testid={`${dataTestId}-detail-close`}
                  >
                    <X className="w-4 h-4 mr-1.5" /> Fermer
                  </Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BonsKpiHeader;
